/**
 * SmartInspect Executive Report — deterministic, content-only PDF component.
 *
 * Renders the frozen finalized report snapshot ({ metadata, answers,
 * roi_result }). Used inside the shared print page (/print/smartinspect/:token)
 * that the backend Playwright renderer loads. No AI, no recompute — every value
 * comes straight from the finalized snapshot produced by the deterministic ROI
 * engine at finalize time.
 *
 * Sprint 9J — the section order now MIRRORS the approved Executive Summary tab
 * (which reuses <SmartInspectResults>): Executive ROI Summary (5 KPI cards) →
 * Current Process vs SmartInspect → Where the Savings Come From → Selected
 * SmartInspect Solution (incl. cost/inspection) → Additional Business Benefits
 * → Supporting Information. This is a static print rendition (no scenario
 * toggle / no action buttons).
 */
import { FileText, Sparkles, Cpu, BarChart3, Check } from "lucide-react";
import { costPerInspectionText } from "../lib/smartInspectApi";

// Qualitative strategic benefits (display only; NOT part of ROI math) —
// kept identical to the on-screen results component for parity.
const STRATEGIC_BENEFITS = [
  { key: "operational-intelligence", title: "Operational Intelligence", icon: Sparkles,
    items: ["PRIVA AI Insights", "Damage Trends", "Carrier Performance", "Root Cause Analysis"] },
  { key: "connected-operations", title: "Connected Operations", icon: Cpu,
    items: ["SAP Integration", "Claims Evidence", "Cross-Team Visibility", "Carrier & Customer Collaboration"] },
  { key: "management", title: "Management", icon: BarChart3,
    items: ["Executive Dashboards", "Better Compliance", "Digital Audit Trail", "Continuous Improvement"] },
];

const ACTIVITY_KEYS = [
  ["paper_report_time", "Paper inspection reports"],
  ["photo_transfer_time", "Photograph transfer"],
  ["photo_upload_time", "Photograph upload"],
  ["manual_data_entry_time", "Manual / Excel data entry"],
  ["report_email_time", "Report preparation & email"],
  ["sap_erp_entry_time", "SAP / ERP data entry"],
];

const fmtCur = (cur, v) => {
  if (v === null || v === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(v);
  } catch { return `${cur} ${Number(v).toLocaleString()}`; }
};
const fmtNum = (v, d = 0) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return "—"; }
};

function Field({ label, value, sub, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="text-sm text-[#111827] font-medium mt-0.5">{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, badge, children, testid, breakBefore = false }) {
  return (
    <section
      data-testid={testid}
      className={`rounded-xl border border-slate-200 bg-white p-5 sm:p-6 ${breakBefore ? "exec-page-break" : ""}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
        {badge && <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 bg-slate-100 rounded-full px-2.5 py-1">{badge}</span>}
      </div>
      {children}
    </section>
  );
}

function KpiCard({ label, value, testid, tone = "default", sub }) {
  const tones = { default: "border-slate-200 bg-white", positive: "border-emerald-200 bg-emerald-50/40" };
  return (
    <div data-testid={testid} className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">{label}</div>
      <div className="text-lg font-bold text-[#111827] mt-1.5 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1 leading-snug">{sub}</div>}
    </div>
  );
}

export function SmartInspectExecutiveReport({ snapshot }) {
  const meta = snapshot?.metadata || {};
  const ans = snapshot?.answers || {};
  const roi = snapshot?.roi_result || {};
  const A = roi.annual_results || {};
  const M = roi.annual_results || {}; // Sprint 9E — annual basis only
  const T = roi.three_year_results || {};
  const pkgSnap = roi.package_pricing_snapshot || {};
  const cur = meta.preferred_currency || roi.preferred_currency || "USD";
  const packageName = meta.selected_package_name || roi.selected_package_name || "—";

  const blocked = !!roi.roi_blocked;
  const customPricing = pkgSnap.custom_pricing || pkgSnap.billing_model === "Custom";

  const locations = Number(ans.operating_locations) || 0;
  const effortPct = (() => {
    const c = Number(A.current_time_per_inspection || 0);
    const s = Number(A.smartinspect_time_per_inspection || 0);
    if (!c || c <= 0) return null;
    return Math.max(0, Math.round(((c - s) / c) * 100));
  })();

  const execSummary = blocked
    ? "Operational savings have been calculated successfully. A valid commercial package is required to calculate ROI, payback and investment returns."
    : `Based on the current inspection process, SmartInspect is estimated to reduce inspection effort by ${effortPct ?? 0}%, delivering approximately ${fmtCur(cur, A.total_direct_savings)} in annual gross operational savings.`;

  const naCur = (v) => (blocked ? "Not Available" : fmtCur(cur, v));
  const roiValue = blocked ? "Not Available" : (A.first_year_roi_percent == null ? "Not Available" : `${fmtNum(A.first_year_roi_percent, 1)}%`);
  const paybackValue = blocked ? "Not Available" : (A.payback_months == null ? "No Payback" : `${fmtNum(A.payback_months, 1)} months`);

  const cmpRows = [
    { label: "Inspection Time", current: M.current_time_per_inspection, si: M.smartinspect_time_per_inspection, type: "min" },
    { label: "Inspection Hours", current: M.current_hours, si: M.smartinspect_hours, type: "hrs" },
    { label: "Total Process Cost", current: M.current_process_cost, si: M.smartinspect_process_cost, type: "cur" },
  ];
  const fmtMetric = (type, v) => {
    if (v === null || v === undefined) return "—";
    if (type === "min") return `${fmtNum(v, 1)} min`;
    if (type === "hrs") return `${fmtNum(v, 0)} hrs`;
    return fmtCur(cur, v);
  };

  const savRows = [
    ["Inspection Labour", M.inspection_labour_savings],
    ["Administration & Reporting", M.administration_savings],
    ["Paper & Printing", M.paper_savings],
    ["Photo Handling", M.photo_savings],
    ["SAP / ERP Entry", M.sap_savings],
    ["Storage & IT", M.storage_it_savings],
    ["Claims & Evidence Preparation", M.claims_savings],
  ];
  if (Number(M.equipment_savings) > 0) savRows.push(["Equipment", M.equipment_savings]);

  const addonLines = A.addon_lines || [];
  const activities = ACTIVITY_KEYS.filter(([k]) => Number(ans[k]) > 0).map(([, label]) => label);
  const monthlySub = roi.monthly_results?.package_subscription;

  return (
    <div className="printable-report space-y-6" data-testid="si-executive-report">
      {/* Report title */}
      <div className="text-center pb-2">
        <div className="inline-flex items-center gap-2 text-[#025979] mb-1">
          <FileText className="w-5 h-5" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]">SmartInspect ROI Business Case</h1>
        <p className="text-sm text-slate-500 mt-1">Prepared for {meta.company_name || "Client"}</p>
      </div>

      {/* 1 — Executive ROI Summary (hero) */}
      <div data-testid="si-report-exec-hero" className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#F5FAFB] to-white p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#025979] font-semibold mb-3">Executive ROI Summary</div>
        <p data-testid="si-report-summary-text" className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl">{execSummary}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mt-7" data-testid="si-report-headline-cards">
          <KpiCard testid="si-report-card-annual" label="Annual Savings" value={fmtCur(cur, A.total_direct_savings)} tone="positive" />
          <KpiCard testid="si-report-card-first-year" label="First-Year Investment" value={A.first_year_investment == null ? "Not Available" : fmtCur(cur, A.first_year_investment)} />
          <KpiCard testid="si-report-card-roi" label="ROI %" value={roiValue} />
          <KpiCard testid="si-report-card-payback" label="Payback Period" value={paybackValue} sub={blocked ? undefined : "First-Year Investment ÷ monthly gross savings"} />
          <KpiCard testid="si-report-card-3yr" label="Three-Year Net Benefit" value={naCur(T.net_benefit)} />
        </div>
      </div>

      {/* 2 — Current Process vs SmartInspect */}
      <Section title="Current Process vs SmartInspect (Annual)" testid="si-report-comparison">
        <div className="flex justify-end -mt-2 mb-2">
          <span className="text-xs text-slate-400">Annual Inspection Volume: <b className="text-slate-600">{fmtNum(A.inspection_volume, 0)}</b></span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase text-slate-400 border-b border-slate-200">
              <th className="text-left py-1.5">Metric</th>
              <th className="text-right">Current</th>
              <th className="text-right">SmartInspect</th>
              <th className="text-right">Savings</th>
            </tr>
          </thead>
          <tbody>
            {cmpRows.map((r) => {
              const cu = Number(r.current);
              const si = Number(r.si);
              const hasVals = Number.isFinite(cu) && Number.isFinite(si);
              const sav = hasVals ? cu - si : null;
              return (
                <tr key={r.label} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-600">{r.label}</td>
                  <td className="text-right text-slate-700">{fmtMetric(r.type, r.current)}</td>
                  <td className="text-right font-medium text-[#025979]">{fmtMetric(r.type, r.si)}</td>
                  <td className="text-right font-bold text-slate-800">{sav === null ? "—" : fmtMetric(r.type, sav)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      {/* 3 — Where the Savings Come From */}
      <Section title="Where the Savings Come From (Annual)" testid="si-report-savings">
        <div className="divide-y divide-slate-100">
          {savRows.filter(([, v]) => Number(v) > 0).map(([label, v]) => (
            <div key={label} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-slate-600">{label}</span>
              <span className="font-medium text-slate-800">{fmtCur(cur, v)}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between py-3 mt-3 bg-emerald-50 rounded-lg px-3.5">
          <span className="font-semibold text-emerald-800 text-sm">Total Gross Direct Savings</span>
          <span className="font-bold text-emerald-800 text-base" data-testid="si-report-total-savings">{fmtCur(cur, M.total_direct_savings)}</span>
        </div>
      </Section>

      {/* 4 — Selected SmartInspect Solution */}
      <Section title="Selected SmartInspect Solution" testid="si-report-solution" breakBefore>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Field label="SmartInspect Package" value={packageName} />
          <Field label="Monthly Inspection Limit"
            value={pkgSnap.unlimited_inspections ? "Unlimited monthly inspections" : (pkgSnap.monthly_inspection_limit != null ? `${fmtNum(pkgSnap.monthly_inspection_limit, 0)} / month` : "—")} />
          <Field label="Monthly Subscription"
            value={customPricing ? "Custom Pricing" : fmtCur(cur, monthlySub)}
            sub={
              customPricing ? undefined :
              `${A.package_subscription != null ? `${fmtCur(cur, A.package_subscription)} / year · ` : ""}${costPerInspectionText({ price: monthlySub, monthlyLimit: pkgSnap.monthly_inspection_limit, unlimited: pkgSnap.unlimited_inspections, custom: customPricing, currencyCode: cur })}`
            } />
          <Field label="Estimated Implementation Investment" value={fmtCur(cur, A.implementation_investment ?? A.deployment_cost)} />
          <Field label="First-Year Investment"
            value={A.first_year_investment == null ? "Not Available" : fmtCur(cur, A.first_year_investment)}
            sub="Subscription + recurring add-ons + implementation + one-time add-ons" />
        </div>
        {addonLines.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-2">Optional Add-ons</div>
            <div className="divide-y divide-slate-100">
              {addonLines.map((a) => (
                <div key={a.name} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-slate-600">{a.name}</span>
                  <span className="font-medium text-slate-800">
                    {a.custom ? "Custom / quote required" : a.one_time != null ? `${fmtCur(cur, a.one_time)} one-time` : `${fmtCur(cur, a.annual)} / year`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* 5 — Additional Business Benefits (qualitative; not in ROI) */}
      <Section title="Additional Business Benefits" badge="Not Included in ROI" testid="si-report-benefits">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {STRATEGIC_BENEFITS.map((g) => (
            <div key={g.key} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <div className="flex items-center gap-2 text-[#025979] text-xs font-semibold mb-3">
                <g.icon className="w-4 h-4" /> {g.title}
              </div>
              <ul className="space-y-1.5">
                {g.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-[13px] text-slate-600">
                    <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* 6 — Supporting Information */}
      <Section title="Supporting Information" testid="si-report-supporting" breakBefore>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <Field label="Company" value={meta.company_name || ans.company_name} />
          <Field label="Contact" value={meta.contact_name || ans.contact_name} />
          <Field label="Industry" value={meta.industry || ans.industry} />
          <Field label="Country" value={meta.country || ans.country} />
          <Field label="Preferred Currency" value={cur} />
          <Field label="Selected SmartInspect Package" value={packageName} />
          <Field label="Estimated Annual Inspection Volume" value={fmtNum(meta.annual_inspection_volume ?? A.inspection_volume)} />
          <Field label="Assessment Date" value={fmtDate(meta.assessment_date)} />
          <Field label="Report Version" value={`v${meta.report_version || 1}`} />
          <Field label="Status" value="Finalized" />
        </div>
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-3">Current Situation</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Field label="Operating Locations" value={fmtNum(locations)} />
            <Field label="Annual Inspections" value={fmtNum(A.inspection_volume)} />
            <Field label="Current Inspection Time" value={`${fmtNum(A.current_time_per_inspection, 1)} min / inspection`} />
            <Field label="Current Inspection Hours (Annual)" value={`${fmtNum(A.current_hours)} hrs`} />
            <Field label="Current Labour Cost (Annual)" value={fmtCur(cur, A.current_labour_cost)} />
          </div>
          {activities.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1.5">Existing Manual Activities</div>
              <div className="flex flex-wrap gap-2">
                {activities.map((a) => (
                  <span key={a} className="inline-flex items-center text-xs bg-slate-100 text-slate-600 rounded-full px-2.5 py-1">{a}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}

export default SmartInspectExecutiveReport;
